import { create } from 'zustand';
import type { AppPlan } from '@/webapp/plan/planTypes';
import type { StyleBrief } from '@/llm/planTypes';
import type { AttachedFile } from '../api/media';
import type { PlanResult } from '../api/build';

/**
 * A document the app is built AROUND — held here only once it is complete.
 *
 * The raw text lives in memory and is never persisted: a book is large, and a blob URL is
 * meaningless the moment the page reloads. What survives a publish is the MARKER on the
 * definition — what the text is called and where it came from.
 */
export interface AttachedDoc {
  title: string;
  attribution: string;
  text: string;
  headingPattern: string;
  /**
   * The file it came from, for the card that shows it. Optional: a doc made before it existed
   * still shows. Kept here, not in the step, because the step's own state went with the trip to
   * Stripe and the card came back reading "Your document" (deep-dive F8).
   */
  fileName?: string;
}

/**
 * A long text picked on the content step that is NOT attached yet — it still needs saying where
 * it came from. Held in the draft rather than the step so that going Back and forth keeps it, and
 * so the plan can warn before somebody walks off the step with a book that will not be built
 * (deep-dive F8: pressing Next without an attribution dropped the file without a word).
 *
 * Memory only, never stashed: up to 4 MB of text, and it is not a choice anybody finished making.
 */
export interface PendingBook {
  fileName: string;
  text: string;
  title: string;
  attribution: string;
  headingPattern: string;
}

/**
 * A short text the page is written FROM — a menu with its prices, a weekend's schedule. The owner's
 * decision (2026-09-30, D1): a .txt or .md of up to 20,000 characters is page content by default,
 * the same thing a file sent in chat after publishing already was. Sent to /build as `documents`.
 */
export interface DraftDocument {
  name: string;
  text: string;
}

/**
 * A picture, clip or sound. `name` is already safe for `S.media('…')` (/^[\w.-]+$/, unique, `.jpg`
 * for a photo); `description` is the line under a photo's thumbnail — the AI's, the person's, or
 * absent — which the build uses for the hero, alt text and captions (owner decision D2).
 *
 * Absent and '' are different on purpose: absent is "nothing said yet", which the AI's line may
 * fill; '' is somebody clearing the box, which nothing fills again.
 */
export type DraftMedia = AttachedFile & {
  description?: string;
  /**
   * The describer thought this photo suits the top of the page (contract C2's `hero`). Only ever
   * `true` or absent: absent is "no opinion", so a photo nobody described never reads as a "no".
   * Kept apart from the line because it is about the photo, not the words — the person's own line
   * does not make the photo any less fit to open the page. Review nit (2026-09-30): the step
   * dropped it, so the prompt's hero mark never fired and the hero rested on words alone.
   */
  hero?: true;
};

/**
 * What someone has said about the app they want, before it exists.
 *
 * Replaces the shared onboardingStore, which carries a dozen fields the web never uses and
 * pulls the mobile storage layer in with it. This holds exactly what the three /new screens
 * write and the build reads, and nothing else.
 *
 * Deliberately NOT persisted. A half-described app is not worth restoring across sessions,
 * and a stale plan reappearing under a new idea is the bug that made the phone's flow
 * confusing — which is why the idea screen clears the plan on every change.
 *
 * Pressing Build is where that stops being true: from then the choices are something the
 * server is charging for, so the build writes them down — a reserved project row and a
 * persisted intent (state/buildIntent.ts) — before it asks. The draft stays memory.
 *
 * One other exception: the trial checkout. Build sends a non-subscriber to Stripe, a full page
 * navigation, so the draft is put away as they go and brought back on the two pages Stripe
 * returns to (state/draftStash.ts) — for that tab, for a day, and never anywhere else.
 */
export interface Draft {
  intent: 'build' | 'browse' | null;
  kind: 'personal' | 'sharable';
  description: string;
  appName: string;
  vibe: string | null;
  plan: AppPlan | null;
  /**
   * The plan the server tailored, and the idea it tailored it for.
   *
   * `/plan` is charged before it answers, so a plan screen that mounts again for the same
   * idea — back from signing in, the browser's back and forward, or Continue pressed again on
   * the idea screen — must not buy the same tailoring twice, nor lay a fresh one over choices
   * the person has made since.
   *
   * Kept apart from `plan`, which is the person's working copy and which the idea screen
   * clears on every Continue: held there, the paid result went with it.
   */
  tailored: { idea: string; plan: AppPlan } | null;
  /**
   * The /plan call in flight, if any, so a screen mounted while it runs waits for THAT answer
   * instead of asking (and paying) again. Memory only, like the rest of the draft.
   */
  tailoring: { idea: string; result: Promise<PlanResult> } | null;
  /** The plan step to reopen on, when the person left it for sign-in or sign-up. */
  planStep: string | null;
  /** Where to resume after a sign-up the build demanded. */
  pendingIntent: 'build' | 'browse' | null;
  /** Only ever set once it has a title AND an attribution — see `documentReady`. */
  attachedDoc: AttachedDoc | null;
  /** A long text picked but not attached yet, for want of an attribution. */
  pendingBook: PendingBook | null;
  /** Short texts the page is written from (D1). Up to 3, 20,000 characters each, 40,000 in all. */
  attachedDocs: DraftDocument[];
  /** Pictures, clips and sounds the app will be written to use BY NAME. */
  attachedMedia: DraftMedia[];
  /**
   * The review step's "Anything else" box — the phone's, which is where the demos said "open with
   * our vineyard photo full width at the top". Put on the plan by `withAnythingElse` at Build.
   */
  anythingElse: string;
  /** The feel of a reference picture. Never its palette — the kit owns colour. */
  styleBrief: StyleBrief | null;

  setIntent: (intent: 'build' | 'browse') => void;
  setKind: (kind: 'personal' | 'sharable') => void;
  setDescription: (description: string) => void;
  setAppName: (appName: string) => void;
  setVibe: (vibe: string | null) => void;
  setPlan: (plan: AppPlan | null) => void;
  setTailored: (tailored: { idea: string; plan: AppPlan } | null) => void;
  setTailoring: (tailoring: { idea: string; result: Promise<PlanResult> } | null) => void;
  setPlanStep: (step: string | null) => void;
  setPendingIntent: (intent: 'build' | 'browse' | null) => void;
  setAttachedDoc: (doc: AttachedDoc | null) => void;
  setPendingBook: (book: PendingBook | null) => void;
  setAttachedDocs: (docs: DraftDocument[]) => void;
  setAttachedMedia: (media: DraftMedia[]) => void;
  setAnythingElse: (words: string) => void;
  setStyleBrief: (brief: StyleBrief | null) => void;
  reset: () => void;
}

const EMPTY = {
  intent: null,
  kind: 'personal',
  description: '',
  appName: '',
  vibe: null,
  plan: null,
  tailored: null,
  tailoring: null,
  planStep: null,
  pendingIntent: null,
  attachedDoc: null,
  pendingBook: null,
  attachedDocs: [],
  attachedMedia: [],
  anythingElse: '',
  styleBrief: null,
} satisfies Omit<
  Draft,
  | 'setIntent' | 'setKind' | 'setDescription' | 'setAppName' | 'setVibe' | 'setPlan'
  | 'setTailored' | 'setTailoring' | 'setPlanStep'
  | 'setPendingIntent' | 'setAttachedDoc' | 'setPendingBook' | 'setAttachedDocs' | 'setAttachedMedia'
  | 'setAnythingElse' | 'setStyleBrief' | 'reset'
>;

/**
 * What belongs to ONE description and goes when it changes (deep-dive F2, 2026-09-30).
 *
 * Build the wedding with five photos, press + and describe the café: the idea box was replaced,
 * and everything else stayed. The café's build was told "the user attached 5 files", the wedding
 * photos were uploaded to the café's site and put on its page, and the only sign was a list of
 * file names on a step the person had no reason to open. Every door to /new/idea (Home, My apps,
 * the tab bar) lands here through the idea screen's Continue, so clearing it HERE closes them all.
 *
 * The same description again keeps everything: back from the plan and Continue pressed a second
 * time is the same app, and its paid tailoring must not be bought again.
 */
const PER_DESCRIPTION = {
  attachedDoc: null,
  pendingBook: null,
  attachedDocs: [],
  attachedMedia: [],
  anythingElse: '',
  styleBrief: null,
  tailored: null,
} satisfies Partial<Draft>;

export const useDraft = create<Draft>((set) => ({
  ...EMPTY,
  setIntent: (intent) => set({ intent }),
  setKind: (kind) => set({ kind }),
  setDescription: (description) =>
    set((s) => (s.description === description ? { description } : { description, ...PER_DESCRIPTION })),
  setAppName: (appName) => set({ appName }),
  setVibe: (vibe) => set({ vibe }),
  setPlan: (plan) => set({ plan }),
  setTailored: (tailored) => set({ tailored }),
  setTailoring: (tailoring) => set({ tailoring }),
  setPlanStep: (planStep) => set({ planStep }),
  setPendingIntent: (pendingIntent) => set({ pendingIntent }),
  setAttachedDoc: (attachedDoc) => set({ attachedDoc }),
  setPendingBook: (pendingBook) => set({ pendingBook }),
  setAttachedDocs: (attachedDocs) => set({ attachedDocs }),
  setAttachedMedia: (attachedMedia) => set({ attachedMedia }),
  setAnythingElse: (anythingElse) => set({ anythingElse }),
  setStyleBrief: (styleBrief) => set({ styleBrief }),
  reset: () => set(EMPTY),
}));
